import { Backdrop } from '@room-match/shared-ui-components';
import InfoContainer from 'components/MyInfo/InfoContainer/InfoContainer';
import { Fragment, useMemo, useState } from 'react';
import { useFormModal } from 'shared/hooks/PetsCars/useFormModal';
import { useSubmitForm } from 'shared/hooks/PetsCars/useSubmitForm';
import { useViewMode } from 'shared/hooks/PetsCars/useViewMode';
import { getAnimalAgeDetails } from 'shared/utils/getAnimalAgeDetails';
import { ILoadingDataProps } from 'shared/interfaces/ILoadingData';
import { ITalentPets, NewPetProps } from 'shared/interfaces/IPets';
import { useAppSelector } from 'shared/redux/store';
import PetList from './PetList/PetList';
import PetsAddEditModal from './PetsAddEditModal/PetsAddEditModal';
import PetViewModal from './PetViewModal/PetViewModal';

const Pets = () => {
  // Get the Pets Data
  const { user: userState } = useAppSelector((state) => state);
  const petsData: ITalentPets[] = useMemo(
    () =>
      userState && userState?.user?.attributes?.pets && Array.isArray(userState?.user?.attributes?.pets)
        ? userState?.user?.attributes?.pets
        : [],
    [userState],
  );

  const [loadingDataInfo, setLoadingDataInfo] = useState<ILoadingDataProps>({
    data: null,
  });
  const preferences = useMemo(() => {
    return userState && userState?.user?.attributes?.preferences ? userState?.user?.attributes?.preferences : null;
  }, [userState]);
  const { view, handleView, viewModeLoading } = useViewMode({
    type: 'pets',
    preferences,
  });
  const handleLoadingDataInfo = (data: NewPetProps) => {
    setLoadingDataInfo({
      data: [
        {
          name: '',
          details: data?.animal,
        },
        {
          name: 'Breed',
          details: data?.breed,
        },
        {
          name: 'Size',
          details: data?.size,
        },
        {
          name: 'Age',
          details: getAnimalAgeDetails({
            age: data.age,
            age_in_months: data?.age_in_months,
          }),
        },
      ],
    });
  };

  const { handleAddItem, handleEditItem, handleDeleteItem, progress } = useSubmitForm<ITalentPets, NewPetProps>({
    data: petsData,
    handleSavingInfo: handleLoadingDataInfo,
    type: 'pets',
  });

  const {
    openFormModal,
    openViewModal,
    formMode,
    selectedItem,
    isOpen,
    isModalDeleteOpen,
    NotificationModal,
    ConfirmationModal,
    handleOpenFormModal,
    handleDeleteModal,
    handleViewModal,
  } = useFormModal({ progress, title: 'Pet' });

  return (
    <Fragment>
      {isOpen ? <NotificationModal /> : <Fragment />}
      {viewModeLoading ? <Backdrop isLoading={viewModeLoading} /> : <Fragment />}
      {openFormModal && formMode ? (
        <PetsAddEditModal
          data={formMode === 'add' ? undefined : petsData[selectedItem.edit]}
          mode={formMode}
          openModal={openFormModal}
          handleOpenModal={handleOpenFormModal}
          handleAddPet={handleAddItem}
          handleEditPet={handleEditItem}
        />
      ) : (
        <Fragment />
      )}
      {isModalDeleteOpen ? (
        <ConfirmationModal
          onConfirm={() => {
            handleDeleteItem({ id: petsData[selectedItem.delete]?.id });
            setLoadingDataInfo({ data: null });
          }}
        />
      ) : (
        <Fragment />
      )}
      {openViewModal ? (
        <PetViewModal
          data={petsData[selectedItem.view]}
          openDialog={openViewModal}
          handleDialog={() => handleViewModal(0)}
        />
      ) : (
        <Fragment />
      )}

      <InfoContainer
        title="Pets"
        button_text={'Add New Pets'}
        viewMode={view}
        onViewClick={handleView}
        onAddFormClick={() => handleOpenFormModal('add')}
      >
        <PetList
          dataList={petsData}
          loadingDataInfo={loadingDataInfo}
          isSaving={progress === 'saving'}
          isEditing={progress === 'editing'}
          isDeleting={progress === 'deleting'}
          selectedEditedItem={selectedItem.edit}
          selectedDeletedItem={selectedItem.delete}
          handleOpenFormModal={handleOpenFormModal}
          handleDeleteModal={handleDeleteModal}
          handleViewModal={handleViewModal}
          variant={view}
        />
      </InfoContainer>
    </Fragment>
  );
};

export default Pets;
